import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Eyebrow, MaskLines, ease } from "./motion";
import { Lightbox } from "./Lightbox";

const TILES = [
  { src: "/img/facade.jpg", area: "md:[grid-column:1/5] md:[grid-row:1/6]" },
  { src: "/img/suite-sea.jpg", area: "md:[grid-column:5/13] md:[grid-row:1/4]" },
  { src: "/img/pool.jpg", area: "md:[grid-column:5/9] md:[grid-row:4/6]" },
  { src: "/img/gym.jpg", area: "md:[grid-column:9/13] md:[grid-row:4/6]" },
  { src: "/img/lobby.jpg", area: "md:[grid-column:1/5] md:[grid-row:6/10]" },
  { src: "/img/lounge.jpg", area: "md:[grid-column:5/10] md:[grid-row:6/8]" },
  { src: "/img/suite-hall.jpg", area: "md:[grid-column:10/13] md:[grid-row:6/10]" },
  { src: "/img/lobby2.jpg", area: "md:[grid-column:5/10] md:[grid-row:8/10]" },
];

const Tile = ({ tile, caption, view, i, onOpen }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      onMouseMove={onMove}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className={`group relative block w-full aspect-[4/3] md:aspect-auto text-start ${tile.area}`}
      data-testid={`gallery-tile-${i}`}
    >
      <motion.div
        variants={{ hidden: { clipPath: "inset(14% 10% 14% 10% round 24px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 24px)", transition: { duration: 1.6, ease, delay: (i % 3) * 0.08 } } }}
        className="absolute inset-0 overflow-hidden rounded-[24px] bg-[#E3F0F0]"
      >
        <motion.img
          variants={{ hidden: { scale: 1.25 }, show: { scale: 1, transition: { duration: 1.6, ease, delay: (i % 3) * 0.08 } } }}
          src={tile.src}
          alt={caption}
          loading="lazy"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]" />
        <div className="spotlight absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 md:p-5 bg-gradient-to-t from-[#0A3A45]/55 to-transparent">
          <span className="text-[12px] uppercase tracking-[0.24em] text-white">{caption}</span>
          <span className="flex items-center gap-1.5 text-[12px] text-white/90 translate-y-2 opacity-0 transition-[opacity,transform] duration-500 group-hover:opacity-100 group-hover:translate-y-0">
            <Plus className="h-3.5 w-3.5" />{view}
          </span>
        </div>
      </motion.div>
    </motion.button>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  return (
    <section id="gallery" className="relative mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-36" data-testid="gallery-section">
      <div className="mb-12 md:mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <Eyebrow>{t.gallery.eyebrow}</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-display text-[clamp(2.4rem,5vw,4.6rem)] leading-[0.98] text-[var(--ink)]">
            <MaskLines key={t.gallery.title} lines={[t.gallery.title]} />
          </h2>
        </div>
        <p className="text-sm text-[var(--ink-soft)] tabular-nums">{String(TILES.length).padStart(2, "0")} / {t.gallery.view}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:gap-5 md:[grid-auto-rows:clamp(110px,10vw,150px)]">
        {TILES.map((tile, i) => (
          <Tile key={tile.src} tile={tile} i={i} caption={t.gallery.captions[i]} view={t.gallery.view} onOpen={() => setOpen(i)} />
        ))}
      </div>
      <Lightbox
        open={open !== null}
        onClose={() => setOpen(null)}
        src={open !== null ? TILES[open].src : ""}
        caption={open !== null ? t.gallery.captions[open] : ""}
        onPrev={() => setOpen((o) => (o - 1 + TILES.length) % TILES.length)}
        onNext={() => setOpen((o) => (o + 1) % TILES.length)}
      />
    </section>
  );
};
